import React from 'react';
import { LocalizationContext, LocalizationContextBuilder, MessageBundle } from '@amzn/arb-tools';
import { useBundle } from '@amzn/react-arb-tools';
import arbManifest from './i18n/arbManifest';
import { readLocaleCookie, writeLocaleCookie } from './localeCookie';
import { MessageBundleManagerOptionsWithDefaultLc } from '@amzn/react-arb-tools/dist/dts/LcConsumingMbmProvider';

export const BUNDLE_NAME = "translations";
export const LOCALE_QUERY_PARAM = 'locale';
const DEFAULT_LOCALE = 'en-US';
const LOCALIZATION_CONTEXTS = new Map<string, LocalizationContext>();

// ARB resolves fr-FR -> fr -> en-US -> en -> und, so no additional sanitization is required
// The cookie is shared with ATBase and IIB; the query parameter is a manual override only
export function resolveLocale(search: string = window.location.search): string {
    return new URLSearchParams(search).get(LOCALE_QUERY_PARAM)
        || readLocaleCookie()
        || document.documentElement.lang
        || DEFAULT_LOCALE;
}

// Writes the locale actually in use back to the shared cookie so ATBase picks it up on the return trip
export function syncLocaleCookie(localizationContext: LocalizationContext) {
    writeLocaleCookie(localizationContext.getLocale());
}

export function getMbmOptions(manifest: typeof arbManifest) {
    const localizationContext = getLocalizationContext(resolveLocale());
    syncLocaleCookie(localizationContext);

    const options: MessageBundleManagerOptionsWithDefaultLc = {
        arbManifest: manifest,
        defaultLocalizationContext: localizationContext,
        urlPrefix: '/translations'
    };

    return options;
}

export function getLocalizationContext(locale: string) {
    let lc = LOCALIZATION_CONTEXTS.get(locale);
    if (!lc) {
        // If the locale is valid but no bundle exists for it, it falls back to the default locale setting
        // If the locale is invalid, the builder uses the last good one or the browser's default setting
        // so we set the "last good one" to English first to force a bad locale tag to English
        lc = new LocalizationContextBuilder()
            .withDefaultLocale(DEFAULT_LOCALE)
            .withLocale(DEFAULT_LOCALE)
            .withLocale(locale)
            .build();
        LOCALIZATION_CONTEXTS.set(locale, lc);
    }

    return lc;
}

// formatMessage returns an array of parts when an arg is a React element
export function t(bundle: MessageBundle, stringId: string, args?: Record<string, string | number>): string | undefined;
export function t(bundle: MessageBundle, stringId: string, args: Record<string, React.ReactNode>): React.ReactNode;
export function t(bundle: MessageBundle, stringId: string, args?: Record<string, unknown>): React.ReactNode {
    if (!bundle || !stringId) {
        return undefined;
    }

    try {
        return args ? bundle.formatMessage(stringId, args) : bundle.getMessage(stringId);
    } catch (e) {
        console.warn('Error fetching message for string ID', e);
        return undefined;
    }
}

export interface WithBundleProps {
    bundle: MessageBundle;
}

// Injects the bundle into class components, which can't call useBundle
export function withBundle<P extends WithBundleProps>(Component: React.ComponentType<P>) {
    const WithBundle = (props: Omit<P, 'bundle'>) => {
        const [bundle, isBundleLoading] = useBundle(BUNDLE_NAME);
        if (isBundleLoading) {
            return null;
        }
        return <Component {...(props as P)} bundle={bundle} />;
    };
    WithBundle.displayName = `withBundle(${Component.displayName || Component.name})`;
    return WithBundle;
}
